import React, { useState, useEffect, useMemo } from "react";
import { useCart } from "../context/CartContext";
import { DashboardHeader } from "./DashboardHeader";
import { initAllStores } from "../utils/cache";
import { sendRequest, errorMessage } from "../utils/api";
import { loadServiceItems } from "../utils/helper-function";
import Service from "../models/Service";
import { useModal } from "../context/ConfirmModalContext";
import { terminal } from "../utils/user-helper";

interface ItemMenuProps {
  title: string;
  items?: Service[];
  categories?: string[];
}

const terminals = [
  { id: "cashPrice", label: "Room" },
  { id: "openBar", label: "Open Bar" },
  { id: "gameHouse", label: "Game House" },
  { id: "vipLounge", label: "Vip Lounge" },
  { id: "privateLounge", label: "Private Lounge" },
  { id: "clubHouse", label: "Club/Restaurant House" },
];

export const ItemMenu: React.FC<ItemMenuProps> = ({ title }) => {

   const [terminalId] = useState(() => terminal());

  const { cart, toggleCart } = useCart();
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);

  const [activeTerminal, setActiveTerminal] = useState("cashPrice");

  // Local state for UI
  const [currentItems, setCurrentItems] = useState<(Service & { itemType: ServiceType })[]>([]);
  const [currentCategories, setCurrentCategories] = useState<string[]>([]);

  // Pagination
  const [page, setPage] = useState(1);
  const ITEMS_PER_PAGE = 9;

  const { openModal, closeModal } = useModal();
  const serviceKey = title.toLowerCase() as ServiceType;

  // Load items from IndexedDB cache
  useEffect(() => {
    const loadFromCache = async () => {
      try {
        const [cachedItems, cachedCategories] = await loadServiceItems(serviceKey);
        setCurrentItems(cachedItems || []);
        setCurrentCategories(cachedCategories || []);

        const cache = await initAllStores();
        const saved = await cache.getBy("data", { id: "lastSyncTime" });
        if (saved[serviceKey]) setLastSyncedAt(new Date(saved[serviceKey]));
      } catch (err) {
        console.error("Failed to load items from cache:", err);
      }
    };

    loadFromCache();
  }, [serviceKey]);

  // Handle synchronization
  const handleSync = async () => {
    try {
      const cache = await initAllStores();
      const lastSyncTime = "2024-01-01T00:00:00.000Z";

      const data = await sendRequest("POST", `/sync/service?terminal=${terminalId}`, {
        [serviceKey]: lastSyncTime,
      });

      const serviceItems = data[serviceKey] || [];

      // Clear and re-save to cache
      await cache.clear(serviceKey);
      await cache.saveMany({ [serviceKey]: serviceItems });

      // Reload into state
      const [updatedItems, updatedCategories] = await loadServiceItems(serviceKey);
      setCurrentItems(updatedItems || []);
      setCurrentCategories(updatedCategories || []);

      const now = new Date().toISOString();
      await cache.updateManyBy("data", ["lastSyncTime"], {
        [serviceKey]: now,
      });

      setLastSyncedAt(new Date());
    } catch (err: any) {
      const error = errorMessage(err.status);
      if (error) {
        return openModal(error.title, error.msg, closeModal, false, "Close");
      } else {
        console.error("Background fetch error:", err);
      }
    }
  };

  // Filter items by category and search
  const filteredItems = useMemo(() => {

    // console.log(search);
    return currentItems.filter((item) => {
      const matchesCategory = selectedCategory ? item.category === selectedCategory : true;
      const matchesSearch = item.name.toLowerCase().includes(search.trim().toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [currentItems, search, selectedCategory]);

  // Paginate filtered results
  const paginatedItems = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredItems.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredItems, page]);

  const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE);

  return (
    <div className="p-6">
      <DashboardHeader
        title={title}
        categories={currentCategories}
        search={search}
        setSearch={setSearch}
        onCategorySelect={setSelectedCategory}
        onSync={() => {
          setSearch("");
          handleSync();
        }}
        lastSyncedAt={lastSyncedAt}
      />

      {/* Terminal / Price Switch */}
      {(title.toLowerCase() === "drinks"
        || title.toLowerCase() === "foods")
        && terminalId === "t2" &&
        <div
          className="flex flex-wrap gap-2 mt-4"
        >
          {terminals.map((terminal) => (
            <button
              key={terminal.id}
              onClick={() => {
                setActiveTerminal(terminal.id);
                // console.log(paginatedItems)
              }
              }
              className={`px-4 py-2 rounded-md text-sm font-sm italic transition
        ${activeTerminal === terminal.id
                  ? "bg-blue-600 text-white shadow"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
            >
              {terminal.label}
            </button>
          ))}
        </div>}

      {/* Items Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-4">
        {paginatedItems.map((item) => {
          const isInCart = cart.some((cartItem) => cartItem.id === item.id);
          return (
            <div
              key={item.id}
              onClick={() =>
                toggleCart({
                  ...item,
                  quantity: 1,
                  itemName: item.name,
                  amount: (item as any)[activeTerminal],
                })
              }
              className={`cursor-pointer p-4 rounded-lg transition ${isInCart
                ? "bg-blue-100 border-2 border-blue-500 shadow-lg"
                : "bg-white shadow hover:shadow-lg"
                }`}
            >
              <h3 className="text-lg font-bold">{item.name}</h3>

              {item.description && (
                <p className="text-gray-500 text-sm mt-1">{item.description}</p>
              )}

              <p className="text-gray-800 font-semibold mt-2">
                ₦{(item as any)[activeTerminal].toLocaleString()}
              </p>
            </div>
          );
        })}
      </div>

      {/* No items found */}
      {filteredItems.length === 0 && (
        <p className="mt-6 text-center text-gray-500">No items found.</p>
      )}

      {/* Simple Pagination Controls (Prev / Next only) */}
      {filteredItems.length > ITEMS_PER_PAGE && (
        <div className="w-full flex justify-center mt-6">
          <div className="flex items-center justify-center gap-3 px-4 py-3 bg-white rounded-lg shadow-sm max-w-fit">
            {/* Prev Button */}
            <button
              disabled={page === 1}
              onClick={() => {
                setPage((p) => Math.max(p - 1, 1));
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className={`px-5 py-2 rounded-md font-medium transition-colors ${page === 1
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-blue-500 text-white hover:bg-blue-600"
                }`}
            >
              Prev
            </button>

            <span className="text-sm text-gray-600 font-medium">
              Page {page} of {totalPages}
            </span>

            {/* Next Button */}
            <button
              disabled={page === totalPages}
              onClick={() => {
                setPage((p) => Math.min(p + 1, totalPages));
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className={`px-5 py-2 rounded-md font-medium transition-colors ${page === totalPages
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-blue-500 text-white hover:bg-blue-600"
                }`}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};